import React, { useRef, useEffect } from "react";
import Globe from "react-globe.gl";
import { VOLCANOES, VOLCANO_ALERTS, MEGATHRUST_ZONES } from "../lib/api";

const depthColor = (d) => (d < 50 ? "#ef4444" : d <= 300 ? "#f97316" : "#10b981");

export default function Globe3D({ quakes, region, layers, wildfires, geoPlates, focusQuake, accent, volcanoes = VOLCANOES }) {
  const globeRef = useRef();

  useEffect(() => {
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat: region.lat, lng: region.lng, altitude: region.altitude }, 1600);
      const controls = globeRef.current.controls();
      controls.autoRotate = false;
      controls.enableDamping = true;
    }
  }, [region]);

  useEffect(() => {
    if (focusQuake && globeRef.current) {
      globeRef.current.pointOfView({ lat: focusQuake.latitude, lng: focusQuake.longitude, altitude: 1.2 }, 1400);
    }
  }, [focusQuake]);

  const points = quakes.map((q) => ({
    lat: q.latitude,
    lng: q.longitude,
    size: Math.max(0.15, q.magnitude * 0.09),
    color: depthColor(q.depth),
    mag: q.magnitude,
    location: q.location,
    depth: q.depth,
    time: q.time,
    altitude: 0.005,
  }));

  // Animated shockwave rings on ALL quakes (magnitude-scaled), on surface.
  const rings = quakes.map((q) => ({
    lat: q.latitude,
    lng: q.longitude,
    maxR: Math.max(2, q.magnitude * 1.4),
    propagationSpeed: 1.6,
    repeatPeriod: 1600,
    color: depthColor(q.depth),
  }));

  const volcanoPoints = layers.volcanoes
    ? volcanoes.map((v) => ({
        lat: v.latitude,
        lng: v.longitude,
        name: v.name,
        alertLevel: v.alertLevel,
        color: (VOLCANO_ALERTS[v.alertLevel] || VOLCANO_ALERTS[1]).color,
      }))
    : [];

  const wildfirePoints = layers.wildfires
    ? wildfires.slice(0, 250).map((w) => {
        const frp = w.frp || 0;
        const highConf = w.confidence === "high";
        const color = (frp > 50 || highConf) ? "#dc2626" : frp >= 20 ? "#f97316" : "#facc15";
        const size = (frp > 50 || highConf) ? 18 : frp >= 20 ? 15 : 12;
        return { lat: w.latitude, lng: w.longitude, color, size, kind: "fire", name: w.title };
      })
    : [];

  const platePaths = layers.plates && geoPlates
    ? geoPlates.features
        .filter((f) => f.geometry.type === "LineString" || f.geometry.type === "MultiLineString")
        .flatMap((f) => {
          const geom = f.geometry;
          const lines = geom.type === "LineString" ? [geom.coordinates] : geom.coordinates;
          return lines.map((coords) => ({ coords, color: "#38bdf8" }));
        })
    : [];

  const megathrustPaths = layers.faults
    ? MEGATHRUST_ZONES.map((z) => ({ coords: z.coords.map(([lat, lng]) => [lng, lat]), color: "#dc2626" }))
    : [];

  const allPaths = [...platePaths, ...megathrustPaths];

  return (
    <div className="h-full w-full relative bg-slate-950" data-testid="globe-3d-container">
      <Globe
        ref={globeRef}
        globeImageUrl="https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
        bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
        backgroundColor="rgba(2,6,23,0)"
        atmosphereColor={accent?.hex || "#06b6d4"}
        atmosphereAltitude={0.22}
        showAtmosphere
        pointsData={[...points]}
        pointLat="lat"
        pointLng="lng"
        pointColor="color"
        pointAltitude="altitude"
        pointRadius="size"
        pointResolution={6}
        pointLabel={(d) =>
          d.mag !== undefined
            ? `<div style="background:#0f172a;border:1px solid #334155;padding:6px 10px;border-radius:6px;color:#e2e8f0;font-family:JetBrains Mono;font-size:11px">
                <b style="color:${d.color}">M ${d.mag.toFixed(1)}</b><br/>${d.location}<br/>Depth: ${d.depth.toFixed(1)} km
               </div>`
            : `<div style="background:#0f172a;border:1px solid #334155;padding:6px 10px;border-radius:6px;color:#e2e8f0;font-family:JetBrains Mono;font-size:11px">${d.name || ""}</div>`
        }
        ringsData={rings}
        ringLat="lat"
        ringLng="lng"
        ringMaxRadius="maxR"
        ringPropagationSpeed="propagationSpeed"
        ringRepeatPeriod="repeatPeriod"
        ringColor={(r) => (t) => `${r.color}${Math.round((1 - t) * 255).toString(16).padStart(2, "0")}`}
        pathsData={allPaths}
        pathPoints="coords"
        pathPointLat={(p) => p[1]}
        pathPointLng={(p) => p[0]}
        pathColor={(p) => p.color}
        pathStroke={1.2}
        pathTransitionDuration={0}
        htmlElementsData={[...volcanoPoints.map((v) => ({ ...v, kind: "volcano" })), ...wildfirePoints]}
        htmlLat="lat"
        htmlLng="lng"
        htmlAltitude={0.005}
        htmlElement={(d) => {
          const el = document.createElement("div");
          el.style.pointerEvents = "auto";
          if (d.kind === "fire") {
            const size = d.size || 15;
            el.style.transform = "translate(-50%, -50%)";
            el.title = d.name || "";
            el.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${d.color}" stroke="#0f172a" stroke-width="1" stroke-linejoin="round">
              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
            </svg>`;
          } else {
            const size = 14 + (d.alertLevel || 1) * 2;
            el.style.transform = "translate(-50%, -100%)";
            el.title = `${d.name} · Level ${d.alertLevel} ${(VOLCANO_ALERTS[d.alertLevel] || {}).label || ""}`;
            el.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 0 4px ${d.color}bb)">
              <polygon points="12,3 22,21 2,21" fill="${d.color}" stroke="#0f172a" stroke-width="1.5" stroke-linejoin="round"/>
              <polygon points="12,8 17,19 7,19" fill="#0f172a" opacity="0.25"/>
            </svg>`;
          }
          return el;
        }}
      />
    </div>
  );
}
